import { fmtDateTime } from '../lib/dates.ts';
import { useEffect, useState } from 'react';
import { search, type SearchHit } from '../lib/api.ts';

/**
 * Debounced so typing does not fire a query per keystroke. 300ms is
 * below the threshold where a search box feels laggy and well above
 * the rate at which anyone types a whole word.
 */
interface Props {
  /** Results have to go somewhere. Text you cannot act on is a
      search that answers "does this exist" and nothing more. */
  readonly onOpenRequest: (id: string) => void;
  readonly onOpenBoard: (siteId: string, categoryId: string) => void;
}

export const Search = ({ onOpenRequest, onOpenBoard }: Props) => {
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (term.trim().length < 2) {
      setHits([]);
      setSearched(false);
      return;
    }

    const id = window.setTimeout(() => {
      search(term)
        .then((r) => {
          setHits(r);
          setSearched(true);
        })
        .catch(() => setHits([]));
    }, 300);

    return () => window.clearTimeout(id);
  }, [term]);

  const when = (iso: string): string =>
    fmtDateTime(iso);

  return (
    <div className="noticeWrap">
      <input
        value={term}
        autoFocus
        placeholder="Search messages and notices"
        onChange={(e) => setTerm(e.target.value)}
      />

      {searched && hits.length === 0 && (
        <p className="empty">Nothing matches that.</p>
      )}

      {hits.length > 0 && (
        <>
          <p className="mono covHead">{hits.length} results</p>
          <ul className="msgList">
            {hits.map((h) => (
              <li
                key={h.id}
                className="msg hit"
                style={{ borderLeftColor: h.color }}
                onClick={() =>
                  h.requestId
                    ? onOpenRequest(h.requestId)
                    : onOpenBoard(h.siteId, h.categoryId)
                }
              >
                <div className="msgMeta">
                  <span className="mono">
                    {h.siteName} &middot; {h.category}
                  </span>
                  <span className="mono">{when(h.createdAt)}</span>
                </div>
                {h.requestTitle && (
                  <p className="hitTitle">{h.requestTitle}</p>
                )}
                <p>{h.body}</p>
                <span className="hitGo">
                  {h.requestId ? 'Open ticket' : 'Show on board'}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
